import { supabase, isSupabaseConfigured } from './supabase'

// Get user's GPS location
export const getUserLocation = () => {
    return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
            reject(new Error('Geolocation is not supported by your browser'))
            return
        }

        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const { latitude, longitude } = position.coords

                // Try to reverse geocode to get city/country
                let city = null
                let country = null

                try {
                    // Using free reverse geocoding API
                    const response = await fetch(
                        `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&accept-language=en`
                    )
                    const data = await response.json()

                    if (data.address) {
                        city = data.address.city || data.address.town || data.address.village || data.address.county || data.address.province || data.address.state
                        country = data.address.country
                    }
                } catch (error) {
                    console.warn('Failed to reverse geocode:', error)
                }

                resolve({ latitude, longitude, city, country })
            },
            (error) => {
                let message = 'Failed to get location'
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        message = 'Location permission denied'
                        break
                    case error.POSITION_UNAVAILABLE:
                        message = 'Location information unavailable'
                        break
                    case error.TIMEOUT:
                        message = 'Location request timed out'
                        break
                }
                reject(new Error(message))
            },
            {
                enableHighAccuracy: true,
                timeout: 30000, // Increased timeout to 30 seconds for fresh lock
                maximumAge: 0, // Force fresh GPS coordinates
            }
        )
    })
}

// Save user location to profiles
export const saveUserLocation = async (userId, location, source = 'gps') => {
    if (!isSupabaseConfigured() || !userId || !location) {
        return { error: 'Invalid parameters' }
    }

    const { error } = await supabase
        .from('profiles')
        .update({
            latitude: location.latitude,
            longitude: location.longitude,
            city: location.city,
            country: location.country,
            location_source: source,
            location_updated_at: new Date().toISOString(),
        })
        .eq('id', userId)

    return { error }
}

// Get and save user location (convenience function)
export const captureUserLocation = async (userId) => {
    try {
        const location = await getUserLocation()
        const { error } = await saveUserLocation(userId, location, 'gps')
        if (error) {
            console.error('❌ Supabase update failed (GPS):', error.message || error)
        }
        return { success: true, location }
    } catch (error) {
        console.warn('Location capture failed:', error.message)
        
        // Fallback to IP-based geolocation if browser geolocation is denied or unavailable
        try {
            console.log('Falling back to IP-based geolocation...')
            const response = await fetch('https://ipapi.co/json/')
            const data = await response.json()
            
            if (data && !data.error) {
                const ipLocation = {
                    latitude: data.latitude || null,
                    longitude: data.longitude || null,
                    city: data.city || null,
                    country: data.country_name || null
                }
                
                const { error: ipError } = await saveUserLocation(userId, ipLocation, 'ip')
                if (ipError) {
                    console.error('❌ Supabase update failed (IP):', ipError.message || ipError)
                }
                return { success: true, location: ipLocation, method: 'ip' }
            }
        } catch (ipError) {
            console.warn('IP Location capture also failed:', ipError.message)
        }
        
        return { success: false, error: error.message }
    }
}
